import React, { useEffect, useState } from "react";

// Routing Components
import { Link, useLocation } from "react-router-dom";
import { Routes } from "@util";

// UI Elements
import { Divide as Hamburger } from "hamburger-react";
import { useDarkMode } from "@hooks/dark-mode";
import { BsSun } from "react-icons/bs";

// Props
import { useUser } from "@hooks/user";
import LanguageSwitcher from "../../components/LanguageSwitcher";
import { useTranslation } from "react-i18next";

// Custom Icons
const MoonIcon = ({ size = 20 }: { size?: number }) => (
  <svg
    aria-hidden="true"
    focusable="false"
    height={size}
    role="presentation"
    viewBox="0 0 24 24"
    width={size}
  >
    <path
      d="M21.53 15.93c-.16-.27-.61-.69-1.73-.49a8.46 8.46 0 01-1.88.13 8.409 8.409 0 01-5.91-2.82 8.068 8.068 0 01-1.44-8.66c.44-1.01.13-1.54-.09-1.76s-.77-.55-1.83-.11a10.318 10.318 0 00-6.32 10.21 10.475 10.475 0 007.04 8.99 10 10 0 002.89.55c.16.01.32.02.48.02a10.5 10.5 0 008.47-4.27c.67-.93.49-1.519.32-1.79z"
      fill="currentColor"
    />
  </svg>
);

function scrollToSection(id: string) {
  const element = document.getElementById(id);
  if (element) {
    const navbarHeight = 80;
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.scrollY - navbarHeight;

    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth"
    });
  }
}
// All Links For The Header View
export const links = ["Overview", "Solutions", "Features", "Pricing"];

export default function Header(props: Drivn.HeaderProps) {
  const { t } = useTranslation();

  /**
   * Navigate To A Specified Link Depending On The Nav Item
   *
   * @param link - The String Element Of The Button
   * @returns The Navigation Link To Navigate To
   */
  const handleNavigate = (value: string): string => {
    let link = value.toLowerCase().replaceAll(" ", "-");
    return location.pathname === "/" ? `#${link}` : `/#${link}`;
  };

  // Is The App Using Dark Mode
  const dark = useDarkMode();

  // The Current Location
  const location = useLocation();

  // The User
  const user = useUser();

  // The Active Tab
  const [active, setActive] = useState("");

  // Listening To Make Active
  useEffect(() => {
    // Check If A Certain Page Was Scrolled To
    const getScrollTo = (int: number): boolean => {
      // The Current Heigh
      const height = window.innerHeight;

      return (
        window.scrollY > height * int &&
        window.scrollY < height * (int + 1) &&
        active !== links[int - 1]
      );
    };

    const changeNavbarColor = () => {
      switch (true) {
        case getScrollTo(1):
          setActive(links[0]);
          break;
        case getScrollTo(2):
          setActive(links[1]);
          break;
        case getScrollTo(3):
          setActive(links[2]);
          break;
        case getScrollTo(4):
          setActive(links[3]);
          break;
        case getScrollTo(5):
          setActive(links[4]);
          break;
        default:
          setActive("");
      }
    };
    window.addEventListener("scroll", changeNavbarColor);
  });

  // Get translated menu items
  const translatedLinks = links.map(link => ({
    original: link,
    translated: t(`navigation.${link.toLowerCase()}`)
  }));

  return (
    <section className={`${props.open ? "overflow-hidden" : ""} m-0 p-0 h-20`}>
      <nav
        className={`${props.open ? "bg-main" : "bg-bg"
          } transition-all w-screen flex items-center h-20 px-4 fixed top-0 z-50 m-0 p-0`}
      >
        <div className="flex w-full items-center">
          <Link to="/" className="mr-4 ml-4 md:ml-10">
            <img src={dark.getImage()} alt="icon" className="w-30 h-10" />
          </Link>

          <ul className="hidden lg:flex items-center list-none text-gray-100 font-semibold">
            {translatedLinks.map(link => (
              <li key={link.original}>
                {location.pathname === "/" ? (
                  <button
                    onClick={() => scrollToSection(link.original.toLowerCase())}
                    className={`active:text-bg p-2 px-3 mx-1 ${active === link.original ? "text-bg bg-main" : "text-black"
                      } outline-2 rounded-3xl hover:text-bg hover:bg-main hover:rounded-3xl transition-all active:bg-black active:bg-text`}
                  >
                    {link.translated}
                  </button>
                ) : (
                  <Link
                    to={handleNavigate(link.original)}
                    className="p-2 px-3 mx-1 text-black outline-2 rounded-3xl hover:text-bg hover:bg-main hover:rounded-3xl transition-all active:bg-black active:bg-text"
                  >
                    {link.translated}
                  </Link>
                )}
              </li>
            ))}
          </ul>

          <div className="ml-auto flex items-center">
            <div className="mr-4 hidden lg:block">
              <button
                onClick={dark.toggle}
                className="text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-4 focus:ring-gray-200 dark:focus:ring-gray-700 rounded-lg text-sm p-2.5"
              >
                {dark.dark ? <MoonIcon size={20} /> : <BsSun size={20} />}
              </button>
            </div>

            <div className="mr-4 hidden lg:block">
              <LanguageSwitcher variant="compact" />
            </div>

            <Link
              to={
                user.signedIn === true
                  ? Routes.Dashboard.path
                  : Routes.Signin.path
              }
              className="hidden lg:block mr-4 p-2 px-3 rounded-3xl text-bg bg-main hover:bg-white hover:text-main active:text-main active:bg-secBg"
            >
              {user.signedIn === true ? t('navigation.dashboard') : t('navigation.login')}
            </Link>
          </div>

          <div className="lg:hidden ml-auto">
            <Hamburger
              toggled={props.open}
              toggle={() => props.toggleOpen(!props.open)}
              color={props.open ? "#111111" : dark.dark ? "#80ABF9" : "#1673FF"}
            />
          </div>
        </div>
      </nav>

      <div
        className={`${props.open
          ? "opacity-100 h-screen items-center mt-20 flex flex-col fixed top-0 left-0 right-0 bottom-0 bg-main z-40"
          : "pointer-events-none opacity-0"
          }`}
      >
        {translatedLinks.map(link => (
          <a
            className="text-white justify-center text-3xl my-4 hover:text-bg font-semibold"
            key={link.original}
            href={handleNavigate(link.original)}
            onClick={() => props.toggleOpen(false)}
          >
            {link.translated}
          </a>
        ))}

        <Link
          className="text-main bg-white text-3xl my-4 hover:text-white hover:bg-transparent font-semibold mt-6 border-2 border-white py-2 px-6 rounded-3xl transition-all"
          to={
            user.signedIn === true ? Routes.Dashboard.path : Routes.Signin.path
          }
          onClick={() => props.toggleOpen(false)}
        >
          {user.signedIn === true ? t('navigation.dashboard') : t('navigation.login')}
        </Link>

        <div className="mt-6">
          <LanguageSwitcher />
        </div>

        <div className="mt-6">
          <button
            onClick={dark.toggle}
            className="text-white text-3xl font-semibold p-4 rounded-lg hover:bg-white hover:text-main transition-all flex items-center justify-center"
          >
            {dark.dark ? <BsSun size={24} /> : <MoonIcon size={24} />}
          </button>
        </div>
      </div>
    </section>
  );
}
